import React, { Component } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themesAnimated from '@amcharts/amcharts4/themes/animated';
import _ from 'lodash';
import * as MD from '@material-ui/core';
import { TypedStorage } from '../../Utils/TypedStorage';
import { History } from 'history';
import { ILocaleInfos, I18N } from '../../Store/I18n/Types';
import am4lang_en_US from "@amcharts/amcharts4/lang/en_US";
import am4lang_fr_FR from "@amcharts/amcharts4/lang/fr_FR";
import { IStatsState } from '../../Store/Statistics/Types';
import { IApplicationState } from '../../Store';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { secondsToHMS } from '../../Utils/Time';
import PersonIcon from '@material-ui/icons/Person';

const styles = (theme:any) => MD.createStyles({
    root: {
      width: '100%',
    },
    paper: {
      width: '100%',
      clear:'both',
      marginBottom: theme.spacing(2),
    },
    table: {
      minWidth: 750,
    },
    toggleDiv:{
      width:'100%',
    },
    thStyle: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.secondary,
        textAlign: 'center',
        textTransform: 'capitalize', 
        fontWeight: 400
    },
    tableContainer: {
        width: '30%',
        border: '1px solid rgb(190, 190, 190)',
        maxHeight: '600px',
        height: 'fit-content'
    },
    tableRightBorder: { 
        borderRightWidth: 1, 
        borderRightStyle: 'groove',
    },
    active: {
        color: '#7863e0',
        fill: theme.palette.primary.main
    },
    text: {
        fill: 'white'
    },
    stepper: {
        marginTop: 10,
        marginBottom: 10,
        paddingTop: 10,
        paddingBottom: 5,
        width: '99%'
    }
  });

  interface IPropsFromState {
    i18n: I18N;
    localeInfos: ILocaleInfos;
    stats: IStatsState;
}
  
interface IPropsFromDispatch {
}
  
interface IOwnProps {
    chartId: string,
    data: any[],
    category: string,
    title: string,
    titleColor: string,
    chartName: string,
    value: string,
    history: History,
    buildStepper: () => void;
}

interface IBarState {
    showPercent: boolean,
    showRepReg: boolean
}
const initialState: IBarState = {
    showPercent: false,
    showRepReg: true
}

type AllProps =
    MD.WithTheme &
MD.WithStyles<typeof styles> &
    IPropsFromState &
    IPropsFromDispatch &
    IOwnProps;


export class BarChartComponent extends Component<AllProps, IBarState> {

    chart: any;
    
    public constructor(props: AllProps) {
        super(props);
        this.state = {...initialState};
    }

    componentDidMount() {
        this.initChart(this.props, this.state);
    }

    componentDidUpdate(prevProps: AllProps, prevState:IBarState) {
        //Handle refreshing the chart when the dataset changes
        if (!_.isEqual(prevProps.data, this.props.data) || !_.isEqual(prevProps.localeInfos.momentLocale, this.props.localeInfos.momentLocale)) {
            if (this.chart) {
                if (this.props.chartId === 'chart3') {
                    let charts = am4core.registry.baseSprites
                    if (charts !== null && charts.length > 1 ) {
                        if (charts[1].svgContainer !== undefined && charts[1].svgContainer.htmlElement.id === 'chart4') {
                            charts[1].dispose()
                        }
                    }
                }
                this.chart.dispose();
            }
            this.initChart(this.props, this.state);
        }
        if (!_.isEqual(prevState.showPercent, this.state.showPercent)) {
            this.chart.dispose();
            this.initChart(this.props, this.state);
        }
        if (!_.isEqual(prevProps.theme, this.props.theme)) {
            this.chart.dispose();
            this.initChart(this.props, this.state);
        }
    }

    componentWillUnmount() {
        if (this.chart) {
            //this.chart.dispose();
        }
    }

    initChart(props:AllProps, state:IBarState) {
        //All of our work goes here now
        const { chartId, data, i18n, localeInfos, theme } = this.props; 
        am4core.useTheme(am4themesAnimated);
        // Create chart instance
        let chart = am4core.create(chartId, am4charts.XYChart);
        if (localeInfos.momentLocale === "en-us"){
            chart.language.locale = am4lang_en_US;
        } else if (localeInfos.momentLocale === "fr") {
            chart.language.locale = am4lang_fr_FR
        }
        // Add data
        chart.data = data;
        chart.logo.hidden = true
        chart.logo.height = -15000

        // Create axes
        let xAxis:any = chart.xAxes;
        xAxis = chart.xAxes.push(new am4charts.CategoryAxis());
        xAxis.dataFields.category = props.category;

        /* style xAxis */
        xAxis.renderer.grid.template.location = 0;
        xAxis.renderer.labels.template.fill = am4core.color(theme.palette.text.primary);
        xAxis.renderer.labels.template.fontSize = '12px';
        xAxis.renderer.minGridDistance = 10;
        xAxis.events.on("sizechanged", function(ev:any) {
            xAxis.renderer.labels.template.rotation = -45;
            xAxis.renderer.labels.template.horizontalCenter = "right";
            xAxis.renderer.labels.template.verticalCenter = "middle";
        });
        xAxis.max = 100
        /* y axis Duration */
        let yAxis = chart.yAxes.push(new am4charts.DurationAxis());
        yAxis.baseUnit = "second";
        yAxis.title.text = i18n._("Total duration");
        yAxis.title.fontSize = "20px";
        yAxis.title.fill = am4core.color(theme.palette.primary.main);
        yAxis.renderer.labels.template.fill = am4core.color(theme.palette.text.primary);
        yAxis.renderer.minGridDistance = 50;
        yAxis.min = 0
        yAxis.strictMinMax = true


        /* Create duration series */
        let durationSeries = chart.series.push(new am4charts.ColumnSeries());
        durationSeries.dataFields.categoryX = props.category;
        durationSeries.tooltipText = "[bold]{categoryX}:[/] {valueY.formatDuration('hh:mm:ss')}";
        
        durationSeries.showOnInit = false

        durationSeries.dataFields.valueY = props.value
        durationSeries.name = props.value

        chart.durationFormatter.durationFormat = "hh':'mm':'ss'";
        
        if (durationSeries.columns) {
            let durationTemplate = durationSeries.columns.template;
            
            if (durationTemplate) {
                durationTemplate.paddingRight = 10
                if (data.length === 1) {
                    durationTemplate.width = am4core.percent(100);
                    xAxis.renderer.cellStartLocation = 0.4
                    xAxis.renderer.cellEndLocation = 0.5
                } else {
                    durationTemplate.width = am4core.percent(100);
                    xAxis.renderer.cellStartLocation = 0
                    xAxis.renderer.cellEndLocation = 0.5
                }
                durationTemplate.events.on("hit", function(ev) {
                    if (ev.target && ev.target.dataItem !== undefined){
                        let data:any = ev.target.dataItem.dataContext as any;
                        if (data) {
                            TypedStorage.set('personId', data.person_id)
                            TypedStorage.set('personName', data.person_name)
                            props.history.push('/statistics')
                        }
                    }
                })    
            }
        }
        
    
        if (durationSeries.tooltip) { durationSeries.tooltip.pointerOrientation = "vertical";}
        // Add a drop shadow filter on columns
        if (durationSeries.columns) {
            let shadow = durationSeries.columns.template.filters.push(new am4core.DropShadowFilter());
            shadow.opacity = 0.1;
        }
        // Make up for the top x axis renderer not actually occupying any space
        chart.topAxesContainer.paddingTop = 30;
        // Make a panning cursor
        chart.cursor = new am4charts.XYCursor();
        /* Create vertical scrollbar and place it before the value axis */
        chart.scrollbarY = new am4core.Scrollbar();
        chart.scrollbarY.parent = chart.leftAxesContainer;
        chart.scrollbarY.toBack();

        /* Create a horizontal scrollbar with previe and place it underneath the date axis */
        let scrollbarX = new am4core.Scrollbar();
        chart.scrollbarX = scrollbarX;
        chart.scrollbarX.parent = chart.bottomAxesContainer;
        
        // Pre-zoom
        chart.events.on("ready", function (ev:any) {
            let startName = ''
            let endName = ''
            let index = 0
            durationSeries.columns.each(function(column) {
                if (column.dataItem) {
                    let data = column.dataItem as any
                    let dataContext:any = data.dataContext
                    if (index === 0) {
                        startName = dataContext.person_name
                    }
                    index ++
                    if (index === 9) {
                        endName = dataContext.person_name
                    }
                }
            })
            xAxis.zoomToCategories(startName, endName, false, true);
        });
        
        this.chart = chart;
    }

    private buildTitle() {
        const { theme } = this.props
        return(
            <div style={{display: 'block', width: '50%'}}>
            <span>
                Répartition par {this.props.chartName} ({this.props.stats.periodTitle})
            </span>
            <div style={{textTransform: 'capitalize', marginTop: '10px'}}>
                <span style=
                    {{
                        backgroundColor: this.props.titleColor, 
                        padding: '10px',
                        color: this.props.titleColor === "rgb(221, 221, 221)" ? theme.palette.text.primary : 'white'
                    }}
                >
                    {this.props.title}
                </span>
            </div>
          </div>
        )
    }

    private handleClick(event:React.MouseEvent<HTMLTableRowElement|SVGSVGElement, MouseEvent>, row:any){
        event.stopPropagation()
        if (row) {
            TypedStorage.set('personId', row.person_id)
            TypedStorage.set('personName', row.person_name)
            this.props.history.push('/statistics')
        }
    }

    private buildLegend() {
        const { classes, i18n, data, theme } = this.props;
        return (
            <MD.TableContainer className={classes.tableContainer}>
                <MD.Table aria-label="simple table">
                    <MD.TableHead>
                        <MD.TableRow>
                        <MD.TableCell 
                        style={{
                            backgroundColor: theme.palette.background.paper,
                            color: theme.palette.text.secondary,
                            textTransform: 'capitalize'
                        }} 
                        className={classes.tableRightBorder}
                        > 
                            {"Intervenant"} 
                        </MD.TableCell>
                        <MD.TableCell className={classes.thStyle} align="right">{i18n._('duration')}</MD.TableCell>
                        </MD.TableRow>
                    </MD.TableHead>
                    <MD.TableBody>
                    {data.map((row:any) => (
                        <MD.TableRow 
                        hover={true}
                        key={row.person_id}
                        style={{cursor: 'pointer'}}
                        onClick={(event: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => this.handleClick(event, row)}
                        >
                            <MD.TableCell 
                            className={classes.tableRightBorder} 
                            component="th" 
                            scope="row"
                            >
                                <div style={{display: 'inline-flex'}}>
                                    <PersonIcon style={{color: theme.palette.primary.main}} />
                                    <span style={{padding: '5px', fontWeight: 400}}>
                                        {row.person_name}
                                    </span> 
                                </div>
                            </MD.TableCell>
                            <MD.TableCell style={{
                                textAlign: 'center', fontWeight: 400
                            }}
                            >
                                {secondsToHMS(row.duration)}
                            </MD.TableCell>
                        </MD.TableRow>
                    ))}
                    </MD.TableBody>
                </MD.Table>
            </MD.TableContainer>
        )
        
    }

    render() {
        return (
            <div style={{ display: 'inline-flex', width: '100%'}}>
                <div style={{width: '70%', paddingLeft: '10px'}}>
                    <div style={{display: 'inline-flex', width: '100%'}}>
                        { this.buildTitle() }
                    </div>
                    { this.props.buildStepper() }
                    <div id={this.props.chartId} style={{ width: "100%", height: "500px" }} />
                </div>
                { this.buildLegend() }
            </div>
        )
    }
}
const mapStateToProps = ( { i18n, ui, stats } : IApplicationState) => ({
    localeInfos: i18n.localeInfos,
    i18n: i18n.i18n,
    ui,
    stats
  });
  
  const mapDispatchToProps = (dispatch: Dispatch) => ({
  });
  
  const BarChart = connect( mapStateToProps, mapDispatchToProps)
    (MD.withTheme(MD.withStyles(styles)(BarChartComponent)));
  
  export { BarChart };